import { HOST, makeGetApiRequest } from "@common/index";
import { ArrayElement } from "@common/utilTypes";
import { InventoryTypeEnum } from "./constants";
import { FilterValue } from "./commonTypes";

export type InventoryMetaData = {
  success: boolean;
  response_data: {
    filters: {
      [key: string]: {
        items: Array<{
          key: number;
          display_name: string;
        }>;
        type: "multiselect" | "day-range" | "select";
        display_name: string;
      };
    };
    columns: Array<{
      name: string;
      type: string;
      label?: string;
      sortable: boolean;
      date?: string;
    }>;
  };
};

const map = {
  [InventoryTypeEnum.Outbound]: "OUTBOUND_INVENTORY",
  [InventoryTypeEnum.Dispatch]: "DISPATCH_INVENTORY",
};

const fetchInventoryMetadata = async ({
  workshopId,
  inventoryType,
}: {
  workshopId: number;
  inventoryType: InventoryTypeEnum;
}) => {
  return makeGetApiRequest<InventoryMetaData>(
    `${HOST}/external/get-filters-for-dashboard-listing`,
    {
      params: { workshop_id: workshopId, inventory_filter_type: map[inventoryType] },
    },
    "Keycloak-Authorization",
    "bearer",
    "refurb"
  ).then((res) => res.data?.response_data);
};

export interface OutboundInventoryListing {
  success: boolean;
  data: Array<{
    lead: {
      id: number;
      created_at: string;
      moved_to_dispatch_at: string;
    };
    car_details: {
      registration_no: string;
      make: string;
      model: string;
      variant: string;
      year: string;
      proc_category: string;
    };
    last_work_type: string;
    workshop: string;
    technician_or_inspector: string;
    is_re_assignable: boolean;
    current_task: string;
    last_status_updated_at: string;
    status: {
      name: string;
      colour_coding: string;
    };
    task_triggered_at: string;
    outbound_car_details: {
      outbound_journey_id: number;
      outbound_refurb_car_id: number;
    };
  }>;
  paginator_data: {
    per_page: number;
    total_pages: number;
    count: number;
    curr_page: number;
  };
}

export type InventoryListingItem = ArrayElement<OutboundInventoryListing["data"]>;

const fetchInventoryListing = async ({
  pageNo,
  pageSize,
  search,
  workshopId,
  inventoryType,
  appliedFilters,
  appliedSort,
}: {
  pageNo: number;
  pageSize: number;
  search: string;
  workshopId: number;
  inventoryType: InventoryTypeEnum;
  appliedFilters: Record<string, FilterValue>;
  appliedSort: string;
}) => {
  const filters = Object.entries(appliedFilters).reduce((list, [key, { type, value }]) => {
    if (type === "day-range" && value) {
      list.push({ key, value: value.join("-") });
    } else if (type === "multiselect" && value && value.length) {
      value.forEach((it) => {
        list.push({ key, value: it });
      });
    } else if (type === "select" && value) {
      list.push({ key, value });
    }

    return list;
  }, []);

  const response = await makeGetApiRequest<OutboundInventoryListing>(
    `${HOST}/external/outbound/get-outbound-refurb-car-listing`,
    {
      params: {
        workshop_id: workshopId,
        inventory_filter_type: map[inventoryType],
        filters: JSON.stringify(filters),
        order_by: appliedSort,
        per_page: pageSize,
        page: pageNo,
        search,
      },
    },
    "Keycloak-Authorization",
    "bearer",
    "refurb"
  );

  return response.data;
};

export { fetchInventoryListing, fetchInventoryMetadata };
